import { useRouter, useSearchParams } from 'next/navigation'
import { isEmpty, omitBy } from 'lodash'
import { useCallback } from 'react'

type CustomPushOptions = {
  /**
   * options.clientSideOnly uses the browser history API to update the URL
   * that is synced with client ONLY component, but doesn't reload the page.
   * When is false, it does a full page reload. (Client + Server)
   * Default is true
   */
  clientSideOnly?: boolean
  /**
   * When true, it will add the new parameters to the existing ones.
   * Note. if a single value needs to be removed, it can be set to an empty string, and will
   * be filtered, use this when you want to remove all existing parameters.
   * Default is true
   */
  addExistingParams?: boolean
  /**
   * When sending addExistingParams as false, you can exclude some parameters from being removed.
   */
  excludeParams?: string[]
}

export const useCustomPush = () => {
  const searchParams = useSearchParams()
  const { push } = useRouter()

  return useCallback(
    (
      newParams: Record<string, string>,
      options: CustomPushOptions = {
        clientSideOnly: true,
        addExistingParams: true,
      },
    ) => {
      let updatedParams = options?.addExistingParams
        ? {
            ...Object.fromEntries(searchParams),
            ...newParams,
          }
        : newParams

      updatedParams = Object.fromEntries(
        Object.entries(updatedParams).filter(
          ([key]) => !options.excludeParams?.includes(key),
        ),
      )
      const filteredParams = omitBy(updatedParams, (value) => isEmpty(value))
      const queryString = new URLSearchParams(filteredParams).toString()

      if (options.clientSideOnly) {
        window.history.pushState(null, '', `?${queryString}`)
      } else {
        push(`?${queryString}`, { scroll: false })
      }
    },
    [searchParams, push],
  )
}
